/**
 * Excel 单据 → 排版模板。
 *
 * 报价单、出库单、对账单，用户手里最多的是一张现成的 Excel。以前只能交给 AI
 * 「照着仿」，合并格和列宽经常走样，还占 AI 次数。Excel 本身就是表格结构，和
 * 自由表格几乎一一对应：合并、列宽、行高、加粗、对齐、底色、字号都能原样搬。
 *
 * 这里只做纯转换（输入是抽象出来的 XSheet，不碰 exceljs），读文件在
 * utils/readXlsx.ts。切分规则：
 *   - 只有一格有字、且没有框线的行（标题、说明、落款）→ 标题 / 文本段落
 *   - 连续的有框线行 → 一张带框自由表格
 *   - 连续的无框线多格行（「客户：xx   日期：xx」）→ 一张无框自由表格
 *   - 合并区不许被切开：跨了两种行的合并区，整片归到一张表里
 * 最后按字段名自动绑定：「客户名称：」右边的空格子填上 {{客户名称}}。
 */
import type { FieldMeta } from '../api/types';
import type { ContractSection, FreeTableCell, FreeTableRow, FreeTableSection } from './types';
import { genId } from './types';
import { findFieldByRef } from './fieldMatch';

export interface XCell {
  text: string;
  bold?: boolean;
  /** 字号，磅。 */
  sizePt?: number;
  /** CSS 色值，已去掉黑色。 */
  color?: string | null;
  /** CSS 色值，已去掉白色 / 无填充。 */
  bg?: string | null;
  align?: 'left' | 'center' | 'right' | null;
  /** 四边任意一边有框线。 */
  border?: boolean;
}

export interface XMerge {
  /** 0 起、闭区间。 */
  r1: number;
  c1: number;
  r2: number;
  c2: number;
}

export interface XSheet {
  name: string;
  /** rows[r][c]，0 起，已裁成用到的范围（左上角对齐到第一行/列有内容的位置）。 */
  cells: (XCell | null)[][];
  /** 列宽，Excel 字符宽度；缺省 8.43。 */
  colWidths: number[];
  /** 行高，磅；null = 自动。 */
  rowHeights: (number | null)[];
  merges: XMerge[];
  /** 表里嵌的图片张数（logo、章）。不导入，只提示。 */
  images: number;
  landscape?: boolean;
}

export interface ImportResult {
  sections: ContractSection[];
  /** 自动绑上的字段数。 */
  bound: number;
  /** 页面基准字号（出现最多的字号）。 */
  baseFontSizePx: number;
  landscape: boolean;
  images: number;
  /** 超出上限被截掉的行数。 */
  truncatedRows: number;
}

const MAX_ROWS = 300;
const MAX_COLS = 30;
const DEFAULT_COL_W = 8.43;

const ptToPx = (pt: number) => (pt * 4) / 3;
/** Excel 列宽按「字符」计：中文算两个字符宽。 */
const textWidth = (t: string) => [...t].reduce((n, ch) => n + (/[\u2e80-\uffff]/.test(ch) ? 2.2 : 1), 0);
/** 一个字符宽按 8px 算（11 号字；苹方的数字比 Excel 的 Calibri 宽，按 7px 算日期会折行），格子左右还各有 6px 内边距。 */
const CHAR_PX = 8;
const CELL_PAD_PX = 12;
const escHtml = (s: string) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

type Kind = 'empty' | 'text' | 'grid_b' | 'grid_n' | 'absorbed';

export function sheetToSections(sheet: XSheet, fieldMetas: FieldMeta[]): ImportResult {
  const truncatedRows = Math.max(0, sheet.cells.length - MAX_ROWS);
  const R = Math.min(sheet.cells.length, MAX_ROWS);
  const C = Math.min(Math.max(0, ...sheet.cells.slice(0, R).map((r) => r.length), sheet.colWidths.length), MAX_COLS);
  const at = (r: number, c: number): XCell | null => sheet.cells[r]?.[c] ?? null;
  const merges = sheet.merges
    .filter((m) => m.r1 < R && m.c1 < C)
    .map((m) => ({ ...m, r2: Math.min(m.r2, R - 1), c2: Math.min(m.c2, C - 1) }));

  // 每格属于哪个合并区（锚点自己也算）
  const owner: (XMerge | null)[][] = Array.from({ length: R }, () => Array<XMerge | null>(C).fill(null));
  for (const m of merges) for (let r = m.r1; r <= m.r2; r++) for (let c = m.c1; c <= m.c2; c++) owner[r][c] = m;
  const isAnchor = (r: number, c: number) => {
    const m = owner[r][c];
    return !m || (m.r1 === r && m.c1 === c);
  };

  // ── 1. 行分类 ──
  const kinds: Kind[] = [];
  for (let r = 0; r < R; r++) {
    let filled = 0;
    let bordered = false;
    for (let c = 0; c < C; c++) {
      const x = at(r, c);
      if (x?.border) bordered = true;
      if (isAnchor(r, c) && x && x.text.trim()) filled++;
    }
    kinds.push(bordered ? 'grid_b' : filled === 0 ? 'empty' : filled === 1 ? 'text' : 'grid_n');
  }
  // 合并区不许被切开
  for (const m of merges) {
    if (m.r2 === m.r1) continue;
    const span = kinds.slice(m.r1, m.r2 + 1);
    if (kinds[m.r1] === 'text' && span.slice(1).every((k) => k === 'empty' || k === 'text')) {
      // 跨两行的大标题：下面几行并进来
      for (let r = m.r1 + 1; r <= m.r2; r++) kinds[r] = 'absorbed';
    } else {
      const k: Kind = span.includes('grid_b') ? 'grid_b' : 'grid_n';
      for (let r = m.r1; r <= m.r2; r++) kinds[r] = k;
    }
  }

  // ── 2. 列宽 ──
  const allW = Array.from({ length: C }, (_, c) => {
    const w = sheet.colWidths[c];
    return Number.isFinite(w) && w > 0 ? w : DEFAULT_COL_W;
  });
  // Excel 里短标签（「客户名称：」「制表人：」）能溢出到右边的空格子，我们的格子
  // 会折行，窄列里就成了一字一行的竖排。表格行里的短文字按长度把列补宽。
  for (let r = 0; r < R; r++) {
    if (kinds[r] !== 'grid_b' && kinds[r] !== 'grid_n') continue;
    for (let c = 0; c < C; c++) {
      const m = owner[r][c];
      if (m && (m.c1 !== m.c2 || m.r1 !== m.r2)) continue;
      const w = textWidth((at(r, c)?.text ?? '').trim());
      if (w > 0 && w <= 18) allW[c] = Math.max(allW[c], w + 1);
    }
  }

  // ── 3. 字号：出现最多的那个当正文字号；整张表比纸宽就整体缩小（同 Excel「缩放到一页宽」）──
  const sizeCount = new Map<number, number>();
  for (let r = 0; r < R; r++)
    for (let c = 0; c < C; c++) {
      const x = at(r, c);
      if (x?.text.trim() && x.sizePt) {
        const k = Math.round(ptToPx(x.sizePt));
        sizeCount.set(k, (sizeCount.get(k) ?? 0) + 1);
      }
    }
  const rawBase = [...sizeCount.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? 14.7;
  const contentPx = ((sheet.landscape ? 297 : 210) - 30) * 3.78;
  const naturalPx = allW.reduce((a, w) => a + w * CHAR_PX + CELL_PAD_PX, 0) * (rawBase / 14.7);
  const scale = Math.min(1, contentPx / naturalPx);
  const size = (pt: number | undefined) => Math.max(9, Math.round((pt ? ptToPx(pt) : rawBase) * scale));
  const basePx = Math.min(16, Math.max(10, Math.round(rawBase * scale)));
  // 列的实际占宽 = 文字宽 + 固定内边距。只按 Excel 字符数分比例，窄列会被内边距吃掉一大块
  const colPx = (c: number) => allW[c] * CHAR_PX * (rawBase / 14.7) * scale + CELL_PAD_PX;

  const sections: ContractSection[] = [];
  let bound = 0;

  const textSection = (r: number) => {
    let x: XCell | null = null;
    let span = 1;
    for (let c = 0; c < C; c++) {
      const cell = at(r, c);
      if (isAnchor(r, c) && cell?.text.trim()) {
        x = cell;
        const m = owner[r][c];
        span = m ? m.c2 - m.c1 + 1 : 1;
        break;
      }
    }
    if (!x) return;
    const px = x.sizePt ? size(x.sizePt) : basePx;
    // 横跨大半张表的一格，Excel 里一般就是居中的标题，只是没设对齐
    const align = x.align ?? (span >= C * 0.6 && C > 1 ? 'center' : 'left');
    const bindRes = bindText(x.text.trim(), fieldMetas);
    bound += bindRes.bound;
    const content = escHtml(bindRes.text).replace(/\{\{([^}]*)\}\}/g, (_, n: string) => `{{${n.replace(/&amp;/g, '&')}}}`);
    if (px >= basePx + 4 || (x.bold && px >= basePx + 2)) {
      sections.push({ id: genId(), type: 'header', content, align, fontSizePx: px, bold: !!x.bold });
    } else {
      sections.push({
        id: genId(),
        type: 'text_block',
        content: x.bold ? `<b>${content}</b>` : content,
        fontSizePx: px !== basePx ? px : null,
        align,
      });
    }
  };

  const tableSection = (r1: number, r2: number, bordered: boolean) => {
    // 去掉整列都空的边缘列（Excel 里常见的左侧窄留白列）
    let c1 = 0;
    let c2 = C - 1;
    const colEmpty = (c: number) => {
      for (let r = r1; r <= r2; r++) {
        const x = at(r, c);
        if (x?.text.trim() || x?.border || x?.bg) return false;
        const m = owner[r][c];
        if (m && (m.c1 !== m.c2 || m.r1 !== m.r2)) return false;
      }
      return true;
    };
    while (c1 < c2 && colEmpty(c1)) c1++;
    while (c2 > c1 && colEmpty(c2)) c2--;
    const cols = c2 - c1 + 1;

    const rows: FreeTableRow[] = [];
    for (let r = r1; r <= r2; r++) {
      const cells: FreeTableCell[] = [];
      for (let c = c1; c <= c2; c++) {
        const x = at(r, c);
        const m = owner[r][c];
        const covered = !!m && !(m.r1 === r && m.c1 === c) && m.r1 >= r1 && m.c1 >= c1;
        const px = x?.sizePt ? size(x.sizePt) : null;
        cells.push({
          id: genId(),
          content: covered ? '' : (x?.text ?? '').replace(/\r\n/g, '\n'),
          colSpan: !covered && m ? Math.min(m.c2, c2) - c + 1 : 1,
          rowSpan: !covered && m ? Math.min(m.r2, r2) - r + 1 : 1,
          align: x?.align ?? 'left',
          bold: !!x?.bold,
          shaded: false,
          merged: covered,
          color: x?.color ?? null,
          bg: x?.bg ?? null,
          fontSizePx: px && px !== basePx ? px : null,
        });
      }
      const h = sheet.rowHeights[r];
      rows.push({ id: genId(), cells, heightMm: h ? Math.round(h * 0.3528 * 10) / 10 : null });
    }
    bound += bindGrid(rows, fieldMetas, bordered);
    const widths = Array.from({ length: cols }, (_, i) => colPx(c1 + i));
    const sum = widths.reduce((a, b) => a + b, 0) || 1;
    const colWidths = widths.map((w) => Math.round((w / sum) * 1000) / 10);
    colWidths[cols - 1] = Math.round((colWidths[cols - 1] + 100 - colWidths.reduce((a, b) => a + b, 0)) * 10) / 10;
    const t: FreeTableSection = {
      id: genId(),
      type: 'free_table',
      colWidths,
      rows,
      borderPx: bordered ? 1 : 0,
      fontSizePx: null,
    };
    sections.push(t);
  };

  // ── 4. 按段生成 ──
  let r = 0;
  while (r < R) {
    const k = kinds[r];
    if (k === 'empty' || k === 'absorbed') {
      r++;
    } else if (k === 'text') {
      textSection(r);
      r++;
    } else {
      let e = r;
      while (e + 1 < R && kinds[e + 1] === k) e++;
      tableSection(r, e, k === 'grid_b');
      r = e + 1;
    }
  }

  return {
    sections,
    bound,
    baseFontSizePx: basePx,
    // 只认 Excel 页面设置里的方向：按列宽猜会把「缩放到一页宽」的竖版单据误判成横版
    landscape: !!sheet.landscape,
    images: sheet.images,
    truncatedRows,
  };
}

/** 「客户名称：」「客户名称:____」→ 补上 {{客户名称}}。 */
function bindText(text: string, fieldMetas: FieldMeta[]): { text: string; bound: number } {
  if (text.includes('{{')) return { text, bound: 0 };
  const m = text.match(/^(.{1,20}?)\s*[：:]\s*(_{2,}|＿{2,}|\s*)$/);
  if (!m) return { text, bound: 0 };
  const f = findFieldByRef(fieldMetas, m[1].trim());
  if (!f) return { text, bound: 0 };
  return { text: `${m[1].trim()}：{{${f.name}}}`, bound: 1 };
}

/**
 * 表格里的标签格：右边紧挨着一个空格子，就把它绑上。
 * 「客户名称」「客户名称：」都认。右边没有格子时：带冒号的、或者在无框的信息行里，
 * 就地补成「标签：{{字段}}」；带框表里不补 —— 那多半是明细表头（「金额」），
 * 改了表头就毁了。
 */
function bindGrid(rows: FreeTableRow[], fieldMetas: FieldMeta[], bordered: boolean): number {
  if (!fieldMetas.length) return 0;
  let n = 0;
  for (const row of rows) {
    const cells = row.cells;
    for (let c = 0; c < cells.length; c++) {
      const cell = cells[c];
      if (cell.merged) continue;
      const raw = cell.content.trim();
      if (!raw || raw.includes('{{') || raw.length > 20) continue;
      const label = raw.replace(/\s*[：:]\s*$/, '');
      const f = findFieldByRef(fieldMetas, label);
      if (!f) continue;
      const next = cells[c + (cell.colSpan || 1)];
      if (next && !next.merged && !next.content.trim()) {
        next.content = `{{${f.name}}}`;
        n++;
      } else if (!next && (/[：:]\s*$/.test(raw) || !bordered)) {
        cell.content = `${label}：{{${f.name}}}`;
        n++;
      }
    }
  }
  return n;
}
